import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import jobService from '../../services/jobService';
import './SavedJob.css';

const SavedJobs = () => {
  const [savedJobs, setSavedJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchSavedJobs();
  }, [filter]);

  const fetchSavedJobs = async () => {
    try {
      setLoading(true);
      const response = await jobService.getSavedJobs({filter});
      setSavedJobs(response.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching saved jobs:', error);
      toast.error('Failed to fetch saved jobs');
      setLoading(false);
    }
  };

  const handleUnsave = async (jobId) => {
    if (!window.confirm('Are you sure you want to remove this job from saved?')) {
      return;
    }

    try {
      await jobService.unsaveJob(jobId);
      toast.success('Job removed from saved');
      fetchSavedJobs();
    } catch (error) {
      console.error('Error removing saved job:', error);
      toast.error(error.response?.data?.message || 'Failed to remove job');
    }
  };

  const formatSalary = (salary) => {
    if (!salary || !salary.min) return 'Not specified';
    const min = salary.min.toLocaleString();
    const max = salary.max ? salary.max.toLocaleString() : '';
    const period = salary.period === 'yearly' ? '/year' : '/month';
    return max ? `${salary.currency} ${min} - ${max}${period}` : `${salary.currency} ${min}${period}`;
  };

  const getTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    const intervals = {
      year: 31536000,
      month: 2592000,
      week: 604800,
      day: 86400,
      hour: 3600,
      minute: 60
    };

    for (const [unit, secondsInUnit] of Object.entries(intervals)) {
      const interval = Math.floor(seconds / secondsInUnit);
      if (interval >= 1) {
        return `${interval} ${unit}${interval > 1 ? 's' : ''} ago`;
      }
    }
    return 'Just now';
  };

  if (loading) {
    return (
      <div className="candidate-saved-job">
        <div className="sj-loading"><div className="sj-spinner"></div><p>Loading…</p></div>
      </div>
    );
  }

  return (
    <div className="candidate-saved-job">
      {/* Header */}
      <div className="sj-header">
        <div>
          <h1 className="sj-title">Saved Jobs</h1>
          <p className="sj-subtitle">{savedJobs.length} job{savedJobs.length !== 1 ? 's' : ''} bookmarked for later</p>
        </div>
        <Link to="/jobs" className="sj-cta-btn">
          <span className="material-symbols-outlined">search</span>Browse More
        </Link>
      </div>

      {savedJobs.length === 0 ? (
        <div className="sj-empty">
          <span className="material-symbols-outlined">bookmark_border</span>
          <h3>No saved jobs yet</h3>
          <p>Bookmark jobs you're interested in to view them here</p>
          <Link to="/jobs" className="sj-cta-btn"><span className="material-symbols-outlined">search</span>Browse Jobs</Link>
        </div>
      ) : (
        <div className="sj-grid">
          {savedJobs.map((savedJob) => {
            const job = savedJob.job;
            return (
              <div key={savedJob._id} className="sj-card">
                <div className="sj-card-top">
                  {job?.companyId?.logo
                    ? <img src={job.companyId.logo} alt={job.companyId.name} className="sj-logo" />
                    : <div className="sj-logo-placeholder">{job?.companyId?.name?.charAt(0) || '?'}</div>
                  }
                  <div className="sj-card-title">
                    <h3>{job?.title}</h3>
                    <p>{job?.companyId?.name}</p>
                  </div>
                  <button className="sj-unsave-btn" onClick={() => handleUnsave(job?._id)} title="Remove">
                    <span className="material-symbols-outlined">bookmark_remove</span>
                  </button>
                </div>

                <div className="sj-card-meta">
                  {(job?.location?.city || job?.workMode) && (
                    <span><span className="material-symbols-outlined">location_on</span>{job?.location?.city || job?.workMode}</span>
                  )}
                  {job?.jobType?.name && (
                    <span><span className="material-symbols-outlined">business_center</span>{job.jobType.name}</span>
                  )}
                  {job?.workMode && (
                    <span><span className="material-symbols-outlined">home_work</span>{job.workMode}</span>
                  )}
                </div>

                {job?.salary && (
                  <div className="sj-salary">
                    <span className="material-symbols-outlined">currency_rupee</span>
                    {formatSalary(job.salary)}
                  </div>
                )}

                {job?.requiredSkills?.length > 0 && (
                  <div className="sj-skills">
                    {job.requiredSkills.slice(0, 4).map(s => (
                      <span key={s._id} className="sj-skill">{s.name}</span>
                    ))}
                    {job.requiredSkills.length > 4 && (
                      <span className="sj-skill sj-skill-more">+{job.requiredSkills.length - 4}</span>
                    )}
                  </div>
                )}

                <div className="sj-card-footer">
                  <span className="sj-saved-time">
                    <span className="material-symbols-outlined">bookmark</span>
                    Saved {getTimeAgo(savedJob.createdAt)}
                  </span>
                  <div className="sj-card-actions">
                    <Link to={`/jobs/${job?._id}`} className="sj-btn-view">
                      <span className="material-symbols-outlined">visibility</span>Details
                    </Link>
                    <Link to={`/jobs/${job?._id}/apply`} className="sj-btn-apply">
                      <span className="material-symbols-outlined">send</span>Apply
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SavedJobs;
